import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { initializeApp } from 'firebase/app';
import axios from 'axios';
import toast from 'react-hot-toast';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Initialize Firebase app
let firebaseApp = null;
let messaging = null;

export function usePushNotifications() {
  const isNativeCapacitor =
    typeof window !== 'undefined' &&
    typeof Capacitor !== 'undefined' &&
    typeof Capacitor.isNativePlatform === 'function' &&
    Capacitor.isNativePlatform() &&
    Capacitor.isPluginAvailable?.('PushNotifications');

  const swSupported = typeof window !== 'undefined' && 'serviceWorker' in navigator;
  const notifSupported = typeof window !== 'undefined' && 'Notification' in window;
  const pushSupported = typeof window !== 'undefined' && 'PushManager' in window;
  const webPushAvailable = !isNativeCapacitor && swSupported && notifSupported && pushSupported;

  const isIOS =
    typeof window !== 'undefined' &&
    (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) &&
    !window.MSStream;

  const isStandalone =
    typeof window !== 'undefined' &&
    (window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true);

  const isIOSPromptNeeded = Boolean(isIOS && !isStandalone);

  const [permissionStatus, setPermissionStatus] = useState(() => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      try {
        return Notification.permission || 'default';
      } catch {
        return 'default';
      }
    }
    return 'default';
  });
  const [tokenRegistered, setTokenRegistered] = useState(false);
  const [supported, setSupported] = useState(Boolean(isNativeCapacitor || webPushAvailable));
  const [channelAvailability, setChannelAvailability] = useState({
    nativeCapacitor: Boolean(isNativeCapacitor),
    nativePushAvailable: Boolean(isNativeCapacitor),
    webPushAvailable: Boolean(webPushAvailable),
    firebaseWebAvailable: false,
    isIOS: Boolean(isIOS),
    isStandalone: Boolean(isStandalone),
    isIOSPromptNeeded: Boolean(isIOSPromptNeeded)
  });


  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (isNativeCapacitor) {
      setSupported(true);
      setChannelAvailability({
        nativeCapacitor: true,
        nativePushAvailable: true,
        webPushAvailable: false,
        firebaseWebAvailable: false
      });

      let registrationListener = null;
      let registrationErrorListener = null;
      let pushReceivedListener = null;

      (async () => {
        try {
          const permStatus = await PushNotifications.checkPermissions();
          const receive = permStatus?.receive || 'prompt';
          setPermissionStatus(receive === 'prompt' ? 'default' : receive);

          registrationListener = await PushNotifications.addListener('registration', async (token) => {
            const value = token?.value || '';
            if (!value) return;

            try {
              await axios.post('/api/push/register-token', {
                token: value,
                device_type: 'android'
              });
              setTokenRegistered(true);
            } catch {
              setTokenRegistered(false);
            }
          });

          registrationErrorListener = await PushNotifications.addListener('registrationError', () => {
            setTokenRegistered(false);
          });

          pushReceivedListener = await PushNotifications.addListener('pushNotificationReceived', (notification) => {
            const title = notification?.title || 'MUNDOXYZ';
            const body = notification?.body || 'Tienes una nueva notificación';
            toast.success(`${title}: ${body}`, { duration: 5000, icon: '🔔' });
          });
        } catch {
          setSupported(false);
        }
      })();

      return () => {
        try {
          registrationListener?.remove();
        } catch {
        }
        try {
          registrationErrorListener?.remove();
        } catch {
        }
        try {
          pushReceivedListener?.remove();
        } catch {
        }
      };
    }

    const swSupported = 'serviceWorker' in navigator;
    const notifSupported = 'Notification' in window;
    const pushSupported = 'PushManager' in window;
    const webPushAvailable = !!(swSupported && notifSupported && pushSupported) && !isNativeCapacitor;
    const firebaseWebAvailable =
      !!process.env.REACT_APP_FIREBASE_API_KEY &&
      !!process.env.REACT_APP_FIREBASE_PROJECT_ID &&
      !!process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID &&
      !!process.env.REACT_APP_FIREBASE_APP_ID &&
      !!process.env.REACT_APP_FIREBASE_VAPID_KEY &&
      !isNativeCapacitor;

    setSupported(webPushAvailable);
    setChannelAvailability({
      nativeCapacitor: isNativeCapacitor,
      nativePushAvailable: false,
      webPushAvailable,
      firebaseWebAvailable,
      isIOS: Boolean(isIOS),
      isStandalone: Boolean(isStandalone),
      isIOSPromptNeeded: Boolean(isIOSPromptNeeded)
    });
    if (!swSupported || !notifSupported || !pushSupported) return;
    if (isNativeCapacitor) return;

    try {
      setPermissionStatus(Notification.permission || 'default');
    } catch {
      setPermissionStatus('default');
    }

    (async () => {
      try {
        let registration;
        if (firebaseWebAvailable) {
          // Firebase FCM SW only
          registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
        } else if (webPushAvailable) {
          // WebPush VAPID SW only
          registration = await navigator.serviceWorker.register('/sw.js');
        }

        if (registration && 'pushManager' in registration) {
          const sub = await registration.pushManager.getSubscription();
          if (sub) {
            setTokenRegistered(true);
          }
        }
      } catch {
        // ignore
      }
    })();
  }, []);


  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const requestPermissionAndRegister = async () => {
    if (isNativeCapacitor) {
      try {
        let permissions = await PushNotifications.checkPermissions();
        if (permissions?.receive === 'prompt') {
          permissions = await PushNotifications.requestPermissions();
        }

        const receive = permissions?.receive || 'prompt';
        setPermissionStatus(receive === 'prompt' ? 'default' : receive);

        if (receive !== 'granted') {
          return { ok: false, reason: 'not_granted' };
        }

        await PushNotifications.register();
        return { ok: true, channel: 'native_fcm' };
      } catch {
        return { ok: false, reason: 'error' };
      }
    }

    if (isIOS && !isStandalone) {
      return {
        ok: false,
        reason: 'ios_pwa_required',
        message: 'Para activar notificaciones en iPhone, primero agrega MundoXYZ a tu pantalla de inicio desde el menú Compartir de Safari.'
      };
    }

    if (!supported) return { ok: false, reason: 'not_supported' };

    let permission = 'default';
    try {
      permission = await Notification.requestPermission();
    } catch {
      // ignore
    }
    setPermissionStatus(permission);

    if (permission !== 'granted') {
      return { ok: false, reason: 'not_granted' };
    }

    const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
    const hasFirebaseConfig =
      !!firebaseConfig.apiKey &&
      !!firebaseConfig.projectId &&
      !!firebaseConfig.messagingSenderId &&
      !!firebaseConfig.appId;

    const anyFirebaseEnvSet =
      !!process.env.REACT_APP_FIREBASE_API_KEY ||
      !!process.env.REACT_APP_FIREBASE_PROJECT_ID ||
      !!process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID ||
      !!process.env.REACT_APP_FIREBASE_APP_ID ||
      !!process.env.REACT_APP_FIREBASE_VAPID_KEY;

    // Path A: Firebase FCM (optional)
    if (hasFirebaseConfig && vapidKey) {
      try {
        if (!firebaseApp) {
          firebaseApp = initializeApp(firebaseConfig);
        }
        if (!messaging) {
          messaging = getMessaging(firebaseApp);
        }

        const registration = await navigator.serviceWorker.ready;

        try {
          registration.active?.postMessage({
            type: 'INIT_FIREBASE',
            config: firebaseConfig
          });
        } catch {
          // ignore
        }

        const token = await getToken(messaging, {
          vapidKey,
          serviceWorkerRegistration: registration
        });

        if (!token) {
          return { ok: false, reason: 'no_token' };
        }

        try {
          await axios.post('/api/push/register-token', {
            token,
            device_type: 'web'
          });
        } catch (err) {
          const status = err?.response?.status;
          if (status === 404) {
            return { ok: false, reason: 'backend_push_disabled' };
          }
          if (status === 401) {
            return { ok: false, reason: 'not_authenticated' };
          }
          return { ok: false, reason: 'backend_error' };
        }
        setTokenRegistered(true);

        onMessage(messaging, (payload) => {
          const title = payload?.notification?.title || payload?.data?.title || 'MUNDOXYZ';
          const body = payload?.notification?.body || payload?.data?.body || 'Tienes una nueva notificación';
          toast.success(`${title}: ${body}`, { duration: 5000, icon: '🔔' });
        });

        return { ok: true, channel: 'fcm' };
      } catch {
        return { ok: false, reason: 'error' };
      }
    }

    if (anyFirebaseEnvSet && (!hasFirebaseConfig || !vapidKey)) {
      console.warn('Firebase env is partially set but missing VAPID key. Falling back to standard WebPush.');
    }

    // Path B: WebPush (VAPID) without Firebase
    try {
      let registration = await navigator.serviceWorker.getRegistration();
      if (!registration) {
        registration = await navigator.serviceWorker.register('/sw.js');
      }
      await navigator.serviceWorker.ready;
      registration = await navigator.serviceWorker.getRegistration();
      if (!registration) {
        registration = await navigator.serviceWorker.ready;
      }

      let publicKey = null;
      try {
        const r = await axios.get('/api/webpush/vapid-public-key');
        publicKey = r?.data?.publicKey || null;
      } catch {
        publicKey = null;
      }

      if (!publicKey) {
        return { ok: false, reason: 'missing_webpush_vapid', message: 'Falta la clave pública VAPID en el servidor.' };
      }

      let subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        try {
          await axios.post('/api/webpush/subscribe', { subscription });
          setTokenRegistered(true);
          return { ok: true, channel: 'webpush' };
        } catch (subErr) {
          try {
            await subscription.unsubscribe();
          } catch (_) {}
        }
      }

      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });

      try {
        await axios.post('/api/webpush/subscribe', {
          subscription
        });
      } catch (err) {
        const status = err?.response?.status;
        if (status === 401) {
          return { ok: false, reason: 'not_authenticated', message: 'Debes iniciar sesión para activar notificaciones.' };
        }
        return { ok: false, reason: 'backend_error', message: err?.response?.data?.error || 'Error al registrar en el servidor.' };
      }

      setTokenRegistered(true);
      return { ok: true, channel: 'webpush' };
    } catch (e) {
      const msg = e?.message || '';
      if (msg.toLowerCase().includes('push service error') || e?.name === 'AbortError') {
        return {
          ok: false,
          reason: 'brave_push_disabled',
          message: 'En Brave: debes activar "Usar servicios de Google para mensajería push" en brave://settings/privacy'
        };
      }
      return { ok: false, reason: 'error', message: msg || 'No se pudo activar las notificaciones en este navegador.' };
    }
  };


  const sendTestPush = async () => {
    try {
      const res = await axios.post('/api/webpush/test');
      return { ok: true, data: res.data };
    } catch (err) {
      return {
        ok: false,
        reason: err?.response?.data?.error || err.message || 'Error al enviar prueba'
      };
    }
  };

  return {
    permissionStatus,
    tokenRegistered,
    supported,
    channelAvailability,
    isIOS,
    isStandalone,
    isIOSPromptNeeded,
    requestPermissionAndRegister,
    sendTestPush
  };
}

